import React, { createContext, useContext, useState, useCallback, useMemo, PropsWithChildren, useEffect } from 'react';
import {
  flightDetailsService,
  FlightDetails,
  perDiemService,
  PerDiem as StoredPerDiem,
} from '../services/supabase';
import { useTrips } from './TripsContext';
import { useExpenses } from './ExpensesContext';
import { frankfurterService } from '../services/frankfurter.service';
import { cityLookupService } from '../services/cityLookup.service';
import { getCountryNameFromCurrency } from '../utils/currencyCountryMapping';
import { isPremiumRateCountry } from '../config/premiumRateCountries';

// Helper function to extract date from flight description text
const extractDateFromText = (text: string, expenseDate?: string): string | null => {
  if (!text) return null;

  const yearFromExpense = expenseDate ? new Date(expenseDate).getFullYear() : new Date().getFullYear();

  const patternsWithYear = [
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})/i,
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})/i,
  ];

  const patternsWithoutYear = [
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})/i,
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*/i,
  ];

  for (const pattern of patternsWithYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = match[0];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        continue;
      }
    }
  }

  for (const pattern of patternsWithoutYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = `${match[0]} ${yearFromExpense}`;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        continue;
      }
    }
  }

  return null;
};

// Interfaces for report data
export interface Flight {
  id: string;
  flight: string;
  class: string;
  cost: number;
  currency: string;
  provider: string;
  paymentMethod: string;
  refund: number;
  date: string;
  convertedAmount: number;
  convertedCurrency: string;
  destinationCountry: string;
  exchangeRate?: number | null;
  conversionDate?: string;
  notes?: string;
}

export interface Accommodation {
  id: string;
  description: string;
  city: string;
  country: string;
  date: string;
  nights: number;
  costPerNight: number;
  currency: string;
  paymentMethod: string;
  refund: number;
  convertedAmount: number;
  convertedCurrency: string;
  exchangeRate?: number | null;
  conversionDate?: string;
  notes?: string;
}

export interface DeductibleExpense {
  id: string;
  description: string;
  category: string;
  date: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  refund: number;
  convertedAmount: number;
  convertedCurrency: string;
  exchangeRate?: number | null;
  conversionDate?: string;
  notes?: string;
}

export interface PerDiem {
  id: string;
  country: string;
  type: 'With Accommodation' | 'Self Accommodation';  // UI-only field for display
  days: number;
  dailyAmountUSD: number;
  daily_rate_usd?: number;
  total_usd?: number;
  total_ils?: number | null;
  exchange_rate_usd_to_ils?: number | null;
  calculation_date?: string | null;
}

interface RefundReportContextType {
  // Data
  flights: Flight[];
  accommodations: Accommodation[];
  deductibleExpenses: DeductibleExpense[];
  perDiems: PerDiem[];
  flightDetailsMap: Record<string, FlightDetails>;

  // Loading states
  loading: boolean;
  perDiemsLoading: boolean;

  // Methods
  loadReportData: (tripId: string) => Promise<void>;
  recalculatePerDiems: () => Promise<void>;
  updateFlightDetails: (expenseId: string, updates: Partial<FlightDetails>) => void;
  retryConversion: (expenseId: string) => Promise<void>;
  updatePerDiemType: (displayRowId: string, newType: 'With Accommodation' | 'Self Accommodation') => Promise<void>;
  updatePerDiemDays: (displayRowId: string, newDays: number) => Promise<void>;
}

const RefundReportContext = createContext<RefundReportContextType | undefined>(undefined);

// Israeli tax regulation per diem rates (in USD)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PER_DIEM_RATE_WITH_ACCOMMODATION_2025 = 100;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PER_DIEM_RATE_WITHOUT_ACCOMMODATION_2025 = 167;
const PER_DIEM_RATE_WITH_ACCOMMODATION = 102;
const PER_DIEM_RATE_WITHOUT_ACCOMMODATION = 171;

function getPerDiemRate(country: string, hasAccommodation: boolean, roundUp: boolean = false): number {
  const baseRate = hasAccommodation
    ? PER_DIEM_RATE_WITH_ACCOMMODATION
    : PER_DIEM_RATE_WITHOUT_ACCOMMODATION;

  if (isPremiumRateCountry(country)) {
    const rateWithIncrease = Math.ceil(baseRate * 1.25);
    return roundUp ? Math.ceil(rateWithIncrease) : rateWithIncrease;
  }

  return baseRate;
}

export const RefundReportProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { currentTrip } = useTrips();
  const { expenses, fetchTripExpenses, updateExpense } = useExpenses();

  // State
  const [flightDetailsMap, setFlightDetailsMap] = useState<Record<string, FlightDetails>>({});
  const [storedPerDiems, setStoredPerDiems] = useState<StoredPerDiem[]>([]);
  const [perDiemsLoading, setPerDiemsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-categorize expenses (flights, accommodations, deductibles)
  const { flights, accommodations, deductibleExpenses } = useMemo(() => {
    const flightsData: Flight[] = [];
    const accommodationsData: Accommodation[] = [];
    const deductibleExpensesData: DeductibleExpense[] = [];

    expenses.forEach((expense) => {
      const category = expense.category?.toLowerCase() || '';

      if (category.includes('flight')) {
        const description = expense.description || 'Flight';
        const flightDetail = flightDetailsMap[expense.id];
        const flightClass = flightDetail?.flight_class || 'Economy';
        const flightDate = flightDetail?.actual_flight_date || extractDateFromText(description, expense.expense_date) || expense.expense_date;

        flightsData.push({
          id: expense.id,
          flight: description,
          class: flightClass,
          cost: expense.amount,
          currency: expense.currency || 'USD',
          provider: 'Unknown',
          paymentMethod: expense.payment_method || 'Card',
          refund: 0,
          date: flightDate,
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
          destinationCountry: flightDetail?.destination_country || '',
          exchangeRate: expense.exchange_rate,
          conversionDate: expense.expense_date,
          notes: expense.notes || undefined,
        });
      } else if (category.includes('accommodation') || category.includes('lodging') || category.includes('hotel')) {
        // Use saved city/country if available, otherwise auto-detect
        let city = expense.city || 'N/A';
        let country = expense.country;

        // If country not saved, try to auto-detect from currency
        if (!country) {
          const globalCurrencies = ['USD', 'EUR'];
          const currency = expense.currency || 'USD';
          const isGlobalCurrency = globalCurrencies.includes(currency.toUpperCase());
          country = isGlobalCurrency ? 'N/A' : getCountryNameFromCurrency(currency);
        }

        accommodationsData.push({
          id: expense.id,
          description: expense.description || 'Accommodation',
          city: city,
          country: country,
          date: expense.expense_date,
          nights: 1,
          costPerNight: expense.amount,
          currency: expense.currency || 'USD',
          paymentMethod: expense.payment_method || 'Card',
          refund: 0,
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
          exchangeRate: expense.exchange_rate,
          conversionDate: expense.expense_date,
          notes: expense.notes || undefined,
        });
      } else {
        deductibleExpensesData.push({
          id: expense.id,
          description: expense.description || expense.category || 'Expense',
          category: expense.category || '',
          date: expense.expense_date,
          amount: expense.amount,
          currency: expense.currency || 'USD',
          paymentMethod: expense.payment_method || 'Card',
          refund: 0,
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
          exchangeRate: expense.exchange_rate,
          conversionDate: expense.expense_date,
          notes: expense.notes || undefined,
        });
      }
    });

    // Sort flights by date
    flightsData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Infer country for accommodations with N/A
    // If there's a clear country from flights, use it
    const countriesFromFlights = new Set(
      flightsData
        .map(f => f.destinationCountry)
        .filter(c => c && c !== 'Israel')
    );

    if (countriesFromFlights.size === 1) {
      const inferredCountry = Array.from(countriesFromFlights)[0];
      accommodationsData.forEach(acc => {
        if (acc.country === 'N/A') {
          acc.country = inferredCountry;
        }
      });
    }

    return {
      flights: flightsData,
      accommodations: accommodationsData,
      deductibleExpenses: deductibleExpensesData,
    };
  }, [expenses, flightDetailsMap]);

  // Map stored per diems to display format (split into 2 rows per country)
  const perDiems = useMemo((): PerDiem[] => {
    const displayRows: PerDiem[] = [];
    const roundUp = currentTrip?.round_up_per_diem || false;

    storedPerDiems.forEach(stored => {
      const daysWithAccommodation = stored.days_with_accommodation || 0;
      const daysWithoutAccommodation = stored.days_without_accommodation || 0;

      // Calculate rates for each type (with rounding if enabled)
      const rateWithAccommodation = getPerDiemRate(stored.country, true, roundUp);
      const rateWithoutAccommodation = getPerDiemRate(stored.country, false, roundUp);

      const totalWithAccommodation = daysWithAccommodation * rateWithAccommodation;
      const totalWithoutAccommodation = daysWithoutAccommodation * rateWithoutAccommodation;

      // Calculate proportional ILS amounts if we have exchange rate
      const exchangeRate = stored.exchange_rate_usd_to_ils;

      const ilsWithAccommodation = exchangeRate ? totalWithAccommodation * exchangeRate : null;
      const ilsWithoutAccommodation = exchangeRate ? totalWithoutAccommodation * exchangeRate : null;

      // Add "With Accommodation" row if there are days
      if (daysWithAccommodation > 0) {
        displayRows.push({
          id: `${stored.id}-with`,
          country: stored.country,
          type: 'With Accommodation',
          days: daysWithAccommodation,
          dailyAmountUSD: rateWithAccommodation,
          daily_rate_usd: rateWithAccommodation,
          total_usd: totalWithAccommodation,
          total_ils: ilsWithAccommodation,
          exchange_rate_usd_to_ils: exchangeRate,
          calculation_date: stored.calculation_date,
        });
      }

      // Add "Self Accommodation" row if there are days
      if (daysWithoutAccommodation > 0) {
        displayRows.push({
          id: `${stored.id}-without`,
          country: stored.country,
          type: 'Self Accommodation',
          days: daysWithoutAccommodation,
          dailyAmountUSD: rateWithoutAccommodation,
          daily_rate_usd: rateWithoutAccommodation,
          total_usd: totalWithoutAccommodation,
          total_ils: ilsWithoutAccommodation,
          exchange_rate_usd_to_ils: exchangeRate,
          calculation_date: stored.calculation_date,
        });
      }
    });

    return displayRows;
  }, [storedPerDiems, currentTrip?.round_up_per_diem]);

  // Load report data (flight details and per diems)
  const loadReportData = useCallback(async (tripId: string) => {
    setLoading(true);
    try {
      // Load expenses
      await fetchTripExpenses(tripId);

      // Load flight details
      try {
        const flightDetails = await flightDetailsService.getByTripId(tripId);
        const detailsMap: Record<string, FlightDetails> = {};
        flightDetails.forEach(detail => {
          detailsMap[detail.expense_id] = detail;
        });
        setFlightDetailsMap(detailsMap);
      } catch (error) {
        console.error('Error loading flight details:', error);
      }

      // Load stored per diems
      try {
        const perDiems = await perDiemService.getByTripId(tripId);
        setStoredPerDiems(perDiems);
      } catch (error) {
        console.error('Error loading per diems:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchTripExpenses]);

  // Calculate and store per diems with historical exchange rates
  const recalculatePerDiems = useCallback(async () => {
    if (flights.length === 0 || !currentTrip?.id) {
      return;
    }

    setPerDiemsLoading(true);

    try {
      interface FlightLeg {
        country: string;
        arrivalDate: Date;
        departureDate: Date | null;
      }

      const legs: FlightLeg[] = [];

      // Process each flight to extract destination and country
      for (let index = 0; index < flights.length; index++) {
        const flight = flights[index];
        let country = flight.destinationCountry;

        // If destination country is not set, try to extract from description
        if (!country) {
          const destinationCity = cityLookupService.extractDestinationCity(flight.flight);
          if (destinationCity) {
            try {
              const lookedUpCountry = await cityLookupService.getCityCountry(destinationCity);
              if (lookedUpCountry) {
                country = lookedUpCountry;
              }
            } catch (error) {
              console.error(`Error looking up country for city ${destinationCity}:`, error);
            }
          }
        }

        if (country && country !== 'Israel') {
          const arrivalDate = new Date(flight.date);
          const nextFlight = flights[index + 1];
          const departureDate = nextFlight ? new Date(nextFlight.date) : null;

          legs.push({
            country,
            arrivalDate,
            departureDate,
          });
        }
      }

      // Build accommodation date map (date -> has accommodation)
      const accommodationDates = new Set<string>();
      accommodations.forEach(acc => {
        // Use the conversion date (expense date) for each accommodation
        if (acc.conversionDate) {
          accommodationDates.add(acc.conversionDate);
        }
      });

      console.log('[PerDiem Debug] Legs:', legs.map(l => ({
        country: l.country,
        arrival: l.arrivalDate.toISOString().split('T')[0],
        departure: l.departureDate?.toISOString().split('T')[0] || 'null',
      })));
      console.log('[PerDiem Debug] Accommodation dates:', Array.from(accommodationDates));
      console.log('[PerDiem Debug] Accommodations:', accommodations.map(a => ({
        description: a.description,
        date: a.date,
        conversionDate: a.conversionDate,
        country: a.country,
      })));

      // Calculate days for each country and track which days have accommodation
      interface CountryData {
        days: number;
        daysWithAccommodation: number;
        daysWithoutAccommodation: number;
        arrivalDate: Date;
        dates: string[]; // All dates in this country
      }
      const countryData: Record<string, CountryData> = {};

      legs.forEach(leg => {
        if (leg.departureDate) {
          // Calculate days INCLUSIVE of both arrival and departure dates
          const startDate = new Date(leg.arrivalDate);
          const endDate = new Date(leg.departureDate);

          // Generate all dates in range (inclusive)
          const dates: string[] = [];
          const currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            dates.push(currentDate.toISOString().split('T')[0]);
            currentDate.setDate(currentDate.getDate() + 1);
          }

          const totalDays = dates.length;
          const daysWithAccommodation = dates.filter(d => accommodationDates.has(d)).length;
          const daysWithoutAccommodation = totalDays - daysWithAccommodation;

          if (!countryData[leg.country]) {
            countryData[leg.country] = {
              days: 0,
              daysWithAccommodation: 0,
              daysWithoutAccommodation: 0,
              arrivalDate: leg.arrivalDate,
              dates: [],
            };
          }

          countryData[leg.country].days += totalDays;
          countryData[leg.country].daysWithAccommodation += daysWithAccommodation;
          countryData[leg.country].daysWithoutAccommodation += daysWithoutAccommodation;
          countryData[leg.country].dates.push(...dates);

          console.log(`[PerDiem Debug] Leg ${leg.country}: dates=[${dates.join(', ')}], withAcc=${daysWithAccommodation}, withoutAcc=${daysWithoutAccommodation}`);

          if (leg.arrivalDate < countryData[leg.country].arrivalDate) {
            countryData[leg.country].arrivalDate = leg.arrivalDate;
          }
        } else {
          console.log(`[PerDiem Debug] Leg ${leg.country}: SKIPPED (no departure date)`);
        }
      });

      // Build a map of existing per diems for comparison
      const existingPerDiemsMap = new Map(
        storedPerDiems.map(pd => [pd.country, pd])
      );

      // Track which countries we've processed
      const processedCountries = new Set<string>();

      // Create and store per diem entries with historical exchange rates
      const perDiemPromises = Object.entries(countryData).map(async ([country, data]) => {
        if (data.days > 0) {
          processedCountries.add(country);

          // Check if we have an existing per diem for this country
          const existing = existingPerDiemsMap.get(country);

          // Never overwrite records the user edited by hand (days or type)
          if (existing?.manually_edited) {
            console.log(`Per diem for ${country}: manually edited, keeping as-is`);
            return null;
          }

          // Only recalculate if days have changed
          const daysMatch = existing &&
            existing.days === data.days &&
            (existing.days_with_accommodation || 0) === data.daysWithAccommodation &&
            (existing.days_without_accommodation || 0) === data.daysWithoutAccommodation;

          if (daysMatch) {
            // Days match - keep existing record (preserves any manual edits to rates)
            console.log(`Per diem for ${country}: days unchanged, keeping existing record`);
            return null;
          }

          // Days changed or no existing record - recalculate
          console.log(`Per diem for ${country}: days changed, recalculating`);

          // Calculate total using separate rates for days with/without accommodation (with rounding if enabled)
          const roundUp = currentTrip?.round_up_per_diem || false;
          const rateWithAccommodation = getPerDiemRate(country, true, roundUp);
          const rateWithoutAccommodation = getPerDiemRate(country, false, roundUp);

          const totalWithAccommodation = data.daysWithAccommodation * rateWithAccommodation;
          const totalWithoutAccommodation = data.daysWithoutAccommodation * rateWithoutAccommodation;
          const totalUSD = totalWithAccommodation + totalWithoutAccommodation;

          // Calculate weighted average rate for display
          const averageRate = data.days > 0 ? totalUSD / data.days : 0;

          const calculationDate = data.arrivalDate.toISOString().split('T')[0];

          let exchangeRate: number | null = null;
          let totalILS: number | null = null;

          try {
            const rates = await frankfurterService.getHistoricalRates(
              calculationDate,
              'USD',
              ['ILS']
            );
            exchangeRate = rates.rates['ILS'];
            if (exchangeRate) {
              totalILS = totalUSD * exchangeRate;
            }
          } catch (error) {
            console.error(`Error fetching historical rate for ${calculationDate}:`, error);
          }

          return perDiemService.upsert({
            trip_id: currentTrip.id,
            country,
            days: data.days,
            days_with_accommodation: data.daysWithAccommodation,
            days_without_accommodation: data.daysWithoutAccommodation,
            daily_rate_usd: averageRate,
            total_usd: totalUSD,
            exchange_rate_usd_to_ils: exchangeRate || undefined,
            total_ils: totalILS || undefined,
            calculation_date: calculationDate,
          });
        }
      });

      await Promise.all(perDiemPromises.filter(Boolean));

      // Delete per diems for countries that no longer exist in the flight list (stale data)
      const deletePromises = storedPerDiems
        .filter(pd => !processedCountries.has(pd.country))
        .map(pd => {
          console.log(`Deleting stale per diem for ${pd.country} (no longer in trip)`);
          return perDiemService.delete(pd.id);
        });

      await Promise.all(deletePromises);

      // Reload per diems from database
      const perDiems = await perDiemService.getByTripId(currentTrip.id);
      setStoredPerDiems(perDiems);
    } catch (error) {
      console.error('Error calculating and storing per diems:', error);
    } finally {
      setPerDiemsLoading(false);
    }
  }, [flights, accommodations, currentTrip?.id, currentTrip?.round_up_per_diem, storedPerDiems]);

  // Don't auto-calculate per diems - let the Refund Report page trigger it manually
  // This prevents unnecessary database operations on other pages
  // useEffect(() => {
  //   recalculatePerDiems();
  // }, [recalculatePerDiems]);

  // Load data when trip changes
  useEffect(() => {
    if (currentTrip?.id) {
      loadReportData(currentTrip.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrip?.id]);

  // Update flight details optimistically
  const updateFlightDetails = (expenseId: string, updates: Partial<FlightDetails>) => {
    setFlightDetailsMap(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        ...updates,
      },
    }));
  };

  // Retry failed currency conversion for an expense
  // Uses `expenses` from context directly to avoid dependency on derived arrays
  const retryConversion = useCallback(async (expenseId: string) => {
    const expense = expenses.find(e => e.id === expenseId);
    if (!expense) return;

    const originalAmount = expense.amount;
    const originalCurrency = (expense.currency || 'USD').toUpperCase();
    const expenseDate = expense.expense_date;

    const rates = await frankfurterService.getHistoricalRates(expenseDate, originalCurrency, ['ILS']);
    const rate = rates.rates['ILS'];

    if (rate) {
      await updateExpense(expenseId, {
        converted_amount: originalAmount * rate,
        converted_currency: 'ILS',
        exchange_rate: rate,
        rate_date: rates.date,
      });
    }
  }, [expenses, updateExpense]);

  // Update per diem type for a display row (merge rows if same country has both types)
  const updatePerDiemType = useCallback(async (displayRowId: string, newType: 'With Accommodation' | 'Self Accommodation') => {
    if (!currentTrip?.id) return;

    // Extract stored per diem ID from display row ID (format: "{storedId}-with" or "{storedId}-without")
    const storedId = displayRowId.replace(/-with$/, '').replace(/-without$/, '');
    const isCurrentlyWith = displayRowId.endsWith('-with');
    const hasAccommodation = newType === 'With Accommodation';

    // If the type didn't actually change, do nothing
    if ((isCurrentlyWith && hasAccommodation) || (!isCurrentlyWith && !hasAccommodation)) return;

    const stored = storedPerDiems.find(sp => sp.id === storedId);
    if (!stored) return;

    const daysWithAccommodation = stored.days_with_accommodation || 0;
    const daysWithoutAccommodation = stored.days_without_accommodation || 0;
    const hasBothTypes = daysWithAccommodation > 0 && daysWithoutAccommodation > 0;

    let newDaysWith: number;
    let newDaysWithout: number;

    if (hasBothTypes) {
      // Merge: move all days to the new type
      const totalDays = daysWithAccommodation + daysWithoutAccommodation;
      newDaysWith = hasAccommodation ? totalDays : 0;
      newDaysWithout = hasAccommodation ? 0 : totalDays;
    } else {
      // Single row: just swap the type
      const currentDays = isCurrentlyWith ? daysWithAccommodation : daysWithoutAccommodation;
      newDaysWith = hasAccommodation ? currentDays : 0;
      newDaysWithout = hasAccommodation ? 0 : currentDays;
    }

    // Recalculate totals
    const roundUp = currentTrip?.round_up_per_diem || false;
    const rateWith = getPerDiemRate(stored.country, true, roundUp);
    const rateWithout = getPerDiemRate(stored.country, false, roundUp);
    const totalUSD = (newDaysWith * rateWith) + (newDaysWithout * rateWithout);
    const totalDays = newDaysWith + newDaysWithout;
    const averageRate = totalDays > 0 ? totalUSD / totalDays : 0;
    const exchangeRate = stored.exchange_rate_usd_to_ils;
    const totalILS = exchangeRate ? totalUSD * exchangeRate : null;

    try {
      await perDiemService.update(storedId, {
        days_with_accommodation: newDaysWith,
        days_without_accommodation: newDaysWithout,
        days: totalDays,
        daily_rate_usd: averageRate,
        total_usd: totalUSD,
        total_ils: totalILS || undefined,
        manually_edited: true,
      });

      // Update local state
      setStoredPerDiems(prev => prev.map(sp =>
        sp.id === storedId
          ? { ...sp, days_with_accommodation: newDaysWith, days_without_accommodation: newDaysWithout, days: totalDays, daily_rate_usd: averageRate, total_usd: totalUSD, total_ils: totalILS, manually_edited: true }
          : sp
      ));
    } catch (error) {
      console.error('Error updating per diem type:', error);
    }
  }, [currentTrip?.id, currentTrip?.round_up_per_diem, storedPerDiems]);

  // Update the number of days for a display row (persisted; 0 removes the row)
  const updatePerDiemDays = useCallback(async (displayRowId: string, newDays: number) => {
    if (!currentTrip?.id) return;
    if (!Number.isFinite(newDays) || newDays < 0) return;

    // Extract stored per diem ID from display row ID (format: "{storedId}-with" or "{storedId}-without")
    const storedId = displayRowId.replace(/-with$/, '').replace(/-without$/, '');
    const isWithRow = displayRowId.endsWith('-with');

    const stored = storedPerDiems.find(sp => sp.id === storedId);
    if (!stored) return;

    const newDaysWith = isWithRow ? newDays : (stored.days_with_accommodation || 0);
    const newDaysWithout = isWithRow ? (stored.days_without_accommodation || 0) : newDays;

    // Recalculate totals
    const roundUp = currentTrip?.round_up_per_diem || false;
    const rateWith = getPerDiemRate(stored.country, true, roundUp);
    const rateWithout = getPerDiemRate(stored.country, false, roundUp);
    const totalUSD = (newDaysWith * rateWith) + (newDaysWithout * rateWithout);
    const totalDays = newDaysWith + newDaysWithout;
    const averageRate = totalDays > 0 ? totalUSD / totalDays : 0;

    // Use the stored exchange rate; if the record never got one (e.g. the rate
    // API was down when it was calculated), fetch it now
    let exchangeRate = stored.exchange_rate_usd_to_ils;
    if (!exchangeRate && stored.calculation_date) {
      try {
        const rates = await frankfurterService.getHistoricalRates(stored.calculation_date, 'USD', ['ILS']);
        exchangeRate = rates.rates['ILS'] || null;
      } catch (error) {
        console.error('Error fetching exchange rate for per diem days update:', error);
      }
    }
    const totalILS = exchangeRate ? totalUSD * exchangeRate : null;

    try {
      await perDiemService.update(storedId, {
        days_with_accommodation: newDaysWith,
        days_without_accommodation: newDaysWithout,
        days: totalDays,
        daily_rate_usd: averageRate,
        total_usd: totalUSD,
        total_ils: totalILS ?? undefined,
        exchange_rate_usd_to_ils: exchangeRate ?? undefined,
        manually_edited: true,
      });

      // Update local state
      setStoredPerDiems(prev => prev.map(sp =>
        sp.id === storedId
          ? { ...sp, days_with_accommodation: newDaysWith, days_without_accommodation: newDaysWithout, days: totalDays, daily_rate_usd: averageRate, total_usd: totalUSD, total_ils: totalILS, exchange_rate_usd_to_ils: exchangeRate, manually_edited: true }
          : sp
      ));
    } catch (error) {
      console.error('Error updating per diem days:', error);
    }
  }, [currentTrip?.id, currentTrip?.round_up_per_diem, storedPerDiems]);

  const value: RefundReportContextType = {
    flights,
    accommodations,
    deductibleExpenses,
    perDiems,
    flightDetailsMap,
    loading,
    perDiemsLoading,
    loadReportData,
    recalculatePerDiems,
    updateFlightDetails,
    retryConversion,
    updatePerDiemType,
    updatePerDiemDays,
  };

  return (
    <RefundReportContext.Provider value={value}>
      {children}
    </RefundReportContext.Provider>
  );
};

export const useRefundReport = (): RefundReportContextType => {
  const context = useContext(RefundReportContext);
  if (!context) {
    throw new Error('useRefundReport must be used within a RefundReportProvider');
  }
  return context;
};
